import * as React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { ImagesContext } from "../../Context";
import CustomChip from "../CustomChip";
import OrganizationInfo from "./OrganizationInfo";

interface Props {
  organization: any;
  billingProvider: any;
  setErrorStatus: (hasErrors: boolean) => void;
  claimStatus: string;
}

const Organization: React.FC<Props> = ({
  organization,
  billingProvider,
  setErrorStatus,
  claimStatus,
}) => {
  const images = React.useContext(ImagesContext);
  const [expanded, setExpanded] = React.useState(false);

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const sameNpiError =
    organization?.facility_npi &&
    billingProvider?.facility_npi &&
    organization.facility_npi === billingProvider.facility_npi;

  const requiredFields = [
    "name",
    "address",
    "city",
    "state",
    "zip",
    "federal_tax_id",
    "facility_npi",
  ];

  const missingFields = requiredFields.filter(
    (field) => !organization?.[field]
  );

  const hasErrors = missingFields.length > 0 || sameNpiError;

  const formatLabel = (field: string) => {
    const mapping: Record<string, string> = {
      name: "Organization Name",
      address: "Address",
      city: "City",
      state: "State",
      zip: "Zip Code",
      federal_tax_id: "Federal Tax ID",
      facility_npi: "Facility NPI",
    };

    return mapping[field] || field;
  };

  React.useEffect(() => {
    setErrorStatus(!hasErrors);
  }, [hasErrors, setErrorStatus]);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "4px",
      }}
    >
      <Box
        className="claim-step-two-list-tile"
        sx={{
          boxShadow: expanded && "0px 7px 7px 0px #00000040",
        }}
      >
        <Box
          sx={{
            display: "flex",
            columnGap: "16px",
            alignItems: "center",
          }}
        >
          <div className="item-icon-container">
            <img
              src={hasErrors ? images.close_icon : images.check_mark_icon}
              alt="Insurance Icon"
              className="action-icon-image"
              style={{ width: 23, maxHeight: 23 }}
            />
          </div>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "10px",
            }}
          >
            <Box display="flex" alignItems="center" columnGap="16px">
              <Typography
                sx={{
                  fontSize: "18px",
                  lineHeight: "18px",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  color: "#4A4442",
                }}
              >
                Organization
              </Typography>
              {hasErrors ? (
                <>
                  {missingFields.map((field) => (
                    <CustomChip
                      key={field}
                      label={`Missing ${formatLabel(field)}`}
                      type="error"
                    />
                  ))}
                  {sameNpiError && (
                    <CustomChip
                      key="npi-match"
                      label="Organization NPI and Billing Provider NPI are same"
                      type="error"
                    />
                  )}
                </>
              ) : (
                <CustomChip label="No Errors" />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "18px",
                fontWeight: 500,
                letterSpacing: "1px",
                color: "#707070",
              }}
            >
              {organization?.name} • Federal Tax ID: #
              {organization?.federal_tax_id} • Organization NPI: #
              {organization?.facility_npi}
            </Typography>
          </Box>
        </Box>

        <IconButton onClick={handleExpand}>
          {expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </IconButton>
      </Box>

      {expanded && (
        <OrganizationInfo
          organization={organization}
          claimStatus={claimStatus}
        />
      )}
    </Box>
  );
};

export default Organization;
